import { InfoCircleFilled } from "@ant-design/icons";
import { DataSource, DataSourceCredentials, DataSourceType, DefaultApi } from "@coeff/api";
import { COLORS, DataSourceTypeDisplayNames } from "@coeff/utils";
import * as React from "react";
import { useEffect, useState } from "react";
import styled from "styled-components";

import { Button } from "../../../common/Button";
import { FlexColumn } from "../../../common/FlexLayout";
import { ImportTypeIcon } from "../../../common/ImportTypeIcon";
import { List } from "../../../common/List";
import { Loader } from "../../../common/Loader";
import { Typography } from "../../../common/Typography";
import { assertIsDefined, pluralize, sentryCapture } from "../../utils";

const { Item } = List;

const DataSourceSelections = styled.div`
  margin: 8px 0;
  border-radius: 6px;
  padding: 12px;
  background-color: ${COLORS.black2} !important;
  cursor: default !important;
`;

const ButtonSection = styled.section`
  width: 100%;
  display: flex;
  padding: 6px 6px;
  background: ${COLORS.white};
  justify-content: center;

  .ant-btn {
    flex: 1;
    margin: 0px 10px;
    max-width: 200px;
  }
`;

const WarningSection = styled.section`
  margin: 24px 0 16px 0;
  background: ${COLORS.orangeBg3};
  border-radius: 6px;
`;

const StyledPanel = styled.div`
  padding: 12px;

  .header {
    display: flex;
    line-height: 20px;

    .sectionIcon {
      color: ${COLORS.orange};
      margin-right: 5px;
    }

    .title {
      margin-left: 5px;
      flex: 1;
      font-weight: bold;
    }
  }

  .body {
    margin: 8px 0 6px 20px;
    line-height: 18px;
    font-size: 14px;
  }
`;

const StyledFooter = styled.div({
  paddingTop: 16,
});

const PageDescription = styled.div`
  text-align: center;
  padding: 16px;
  font-style: normal;
  font-size: 14px;
  line-height: 18px;
  color: ${COLORS.black45};

  h3 {
    margin: 14px 0;
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 20px;
    color: ${COLORS.black85};
  }
`;

const StyledItem = styled(Item)`
  && {
    border: none;
    padding: 4px 0;

    .ant-list-item-meta {
      align-items: center;
    }

    .ant-list-item-meta-title {
      color: ${COLORS.black45};
      line-height: 1.25;
      margin-bottom: 0;

      h4 {
        margin: 0px;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
      }
    }

    .ant-list-item-meta-description {
      color: ${COLORS.black85};
      font-size: 14px;
      font-weight: bold;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
  }
`;

const StyledInfoContainer = styled.div`
  background-color: white;
  padding: 12px;
  border-radius: 4px;
  margin-top: 12px;
`;

const CancelButton = styled(Button)`
  && {
    background: none;
    border: none;
    color: ${COLORS.coeblue4};
    font-weight: 600;
    box-shadow: none;

    &:hover,
    &:focus {
      background: none;
      border: none;
      color: ${COLORS.coeblue4};
      opacity: 0.8;
    }

    &:disabled {
      background: none;
      border: none;
      color: ${COLORS.black25};
    }
  }
`;

export type Props = {
  style?: React.CSSProperties;
  existingDataSourceUniqueId: string;
  newDataSourceUniqueId: string;
  dataSourceType: DataSourceType;
  dataSourceId: string;
  ppressMetadata?: Record<string, string>;
  credentials?: DataSourceCredentials;
  additionalDisplayFields?: Record<string, any>;
  existingDataSource?: DataSource;
  isFetchingDataSource?: boolean;
  api: DefaultApi;
  track: (eventName: string, properties?: Record<string, any>) => void;
  onSuccess: (dataSource: DataSource) => void;
  onError: (error: unknown) => void;
  onCancel: () => void;
};

export const DataSourceChangeConfirmationContent: React.FC<Props> = ({
  style,
  existingDataSourceUniqueId,
  newDataSourceUniqueId,
  dataSourceType,
  dataSourceId,
  ppressMetadata,
  credentials,
  additionalDisplayFields,
  existingDataSource,
  isFetchingDataSource = false,
  api,
  track,
  onSuccess,
  onError,
  onCancel,
}) => {
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    track("change_data_source_viewed", {
      event_from: "changeDataSourceConfirmation",
    });
  }, [track]);

  const updateDataSource = async () => {
    try {
      setLoading(true);
      assertIsDefined(dataSourceType);
      assertIsDefined(dataSourceId);
      assertIsDefined(credentials);

      const payload = {
        data_source: {
          data_source_id: dataSourceId,
          // Setting data source name to empty string to avoid updating already existing name.
          // This logic is handled in the BE.
          data_source_name: "",
          data_source_type: dataSourceType,
          credentials: credentials,
          ppress_metadata: ppressMetadata,
        },
      };

      const { data: response } = await api.updateDataSource({
        data_source_id: dataSourceId,
        update_data_source_request: payload,
      });

      track("change_data_source_success", {
        event_from: "changeDataSourceConfirmation",
        data_source_id: dataSourceId,
        original_data_source_label: existingDataSourceUniqueId,
        new_data_source_label: newDataSourceUniqueId,
      });

      onSuccess(response.data_source);
    } catch (error) {
      sentryCapture({
        context: { data_source_id: dataSourceId },
        error,
        name: "DataSourceChangeConfirmation: PUT /api/data_sources/<data_source_id>",
      });

      track("change_data_source_failure", {
        event_from: "changeDataSourceConfirmation",
        data_source_id: dataSourceId,
        original_data_source_label: existingDataSourceUniqueId,
        new_data_source_label: newDataSourceUniqueId,
      });

      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const warningCardTitle = () => {
    const importCount =
      (existingDataSource?.data_source_stats?.num_data_imports ?? 0) +
      (existingDataSource?.data_source_stats?.num_shared_data_imports ?? 0);

    return importCount > 0
      ? pluralize("import", importCount, true) + " will be affected"
      : "Imports will be affected";
  };

  const getConnectionDescription = (dataSourceType: DataSourceType) => {
    if (dataSourceType === DataSourceType.Quickbooks) {
      return "company";
    }

    return "account";
  };

  return (
    <FlexColumn style={{ ...style, justifyContent: "space-between" }}>
      <div>
        <PageDescription>
          <h3>Please Confirm</h3>

          <div>
            {`You reconnected to ${DataSourceTypeDisplayNames[dataSourceType]} using a different account. Are you sure you want to change this account?`}
          </div>
        </PageDescription>

        <DataSourceSelections>
          <StyledItem onClick={() => null}>
            <Item.Meta
              title={`Previous connected ${getConnectionDescription(dataSourceType)}`}
              avatar={<ImportTypeIcon type={dataSourceType} />}
              description={existingDataSourceUniqueId}
            />
          </StyledItem>

          {Object.entries(additionalDisplayFields?.old_connection_details || {}).map(
            (additionalField, index) => (
              <StyledInfoContainer key={index}>
                <Typography color="textSecondary" gutterBottom>
                  {additionalField[0]}
                </Typography>
                <Typography fontWeight="bold">{additionalField[1]}</Typography>
              </StyledInfoContainer>
            )
          )}
        </DataSourceSelections>

        <div style={{ margin: "8px 0", textAlign: "center" }}>to</div>

        <DataSourceSelections>
          <StyledItem onClick={() => null}>
            <Item.Meta
              title={`New connected ${getConnectionDescription(dataSourceType)}`}
              avatar={<ImportTypeIcon type={dataSourceType} />}
              description={newDataSourceUniqueId}
            />
          </StyledItem>

          {Object.entries(additionalDisplayFields?.new_connection_details || {}).map(
            (additionalField, index) => (
              <StyledInfoContainer key={index}>
                <Typography color="textSecondary" gutterBottom>
                  {additionalField[0]}
                </Typography>
                <Typography fontWeight="bold">{additionalField[1]}</Typography>
              </StyledInfoContainer>
            )
          )}
        </DataSourceSelections>

        {!isFetchingDataSource && existingDataSource && (
          <WarningSection>
            <StyledPanel>
              <div className="header">
                <div className="sectionIcon">
                  <InfoCircleFilled />
                </div>
                <div className="title">{warningCardTitle()}</div>
              </div>
              <div className="body">
                {`These imports will use the new ${DataSourceTypeDisplayNames[dataSourceType]} account and may have errors and need to be adjusted.`}
              </div>
            </StyledPanel>
          </WarningSection>
        )}
      </div>

      <StyledFooter>
        <ButtonSection>
          <Button disabled={loading} type="primary" onClick={() => updateDataSource()}>
            {loading ? (
              <Loader color="white" size="micro" />
            ) : (
              `Yes, change ${getConnectionDescription(dataSourceType)}`
            )}
          </Button>
        </ButtonSection>

        <ButtonSection>
          <CancelButton type="primary" disabled={loading} onClick={onCancel}>
            Cancel
          </CancelButton>
        </ButtonSection>
      </StyledFooter>
    </FlexColumn>
  );
};
